// ============================================================================
// Form I-9 Employment Eligibility Verification
// Complete implementation with Section 1, Section 2, document upload & tracking
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  FileText,
  Check,
  AlertTriangle,
  Clock,
  Shield,
  User,
  Building,
  Upload,
  Calendar,
  ChevronRight,
  ChevronLeft,
  Save,
  Send,
  Info,
  Lock,
  Unlock,
  Eye,
  CheckCircle2,
  XCircle,
  HelpCircle,
} from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import ESignature, { SignatureDisplay } from '../compliance/ESignature';
import DocumentUploader, { I9DocumentUploader, DocumentStatusBadge } from '../compliance/DocumentUploader';
import {
  I9_LIST_A_DOCUMENTS,
  I9_LIST_B_DOCUMENTS,
  I9_LIST_C_DOCUMENTS,
  I9_CITIZENSHIP_STATUS,
  I9_STATUS,
  calculateI9Section2DueDate,
  validateI9Documents,
} from '@/lib/complianceData';

// ============================================================================
// US STATES LIST
// ============================================================================

const US_STATES = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' }, { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' }, { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' }, { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' }, { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' }, { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' }, { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' }, { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' }, { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' }, { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' }, { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' }, { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' }, { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' }, { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' }, { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' }, { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' }, { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' }, { code: 'DC', name: 'District of Columbia' },
];

// ============================================================================
// FORM FIELD COMPONENT
// ============================================================================

function FormField({ label, required, error, children, helpText }) {
  return (
    <div className="space-y-1">
      <Label className="flex items-center gap-1">
        {label}
        {required && <span className="text-red-500">*</span>}
        {helpText && (
          <span className="ml-1 text-gray-400 cursor-help" title={helpText}>
            <HelpCircle className="h-3 w-3" />
          </span>
        )}
      </Label>
      {children}
      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
}

// ============================================================================
// SECTION 1: EMPLOYEE INFORMATION
// ============================================================================

function I9Section1({
  formData,
  onChange,
  onSave,
  onSubmit,
  errors = {},
  disabled = false,
  employee,
}) {
  const handleChange = (field, value) => {
    onChange({ ...formData, [field]: value });
  };

  const handleAddressChange = (field, value) => {
    onChange({
      ...formData,
      address: { ...formData.address, [field]: value },
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg">
        <User className="h-6 w-6 text-blue-600" />
        <div>
          <h3 className="font-semibold text-blue-900">Section 1: Employee Information and Attestation</h3>
          <p className="text-sm text-blue-700">
            Employees must complete and sign Section 1 no later than the first day of employment.
          </p>
        </div>
      </div>

      {/* Personal Information */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Personal Information</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FormField label="Last Name (Family Name)" required error={errors.lastName}>
              <Input
                value={formData.lastName || ''}
                onChange={(e) => handleChange('lastName', e.target.value)}
                placeholder="Enter last name"
                disabled={disabled}
              />
            </FormField>
            <FormField label="First Name (Given Name)" required error={errors.firstName}>
              <Input
                value={formData.firstName || ''}
                onChange={(e) => handleChange('firstName', e.target.value)}
                placeholder="Enter first name"
                disabled={disabled}
              />
            </FormField>
            <FormField label="Middle Initial">
              <Input
                value={formData.middleInitial || ''}
                onChange={(e) => handleChange('middleInitial', e.target.value.slice(0, 1))}
                placeholder="M.I."
                maxLength={1}
                disabled={disabled}
              />
            </FormField>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField label="Other Last Names Used (if any)">
              <Input
                value={formData.otherLastNames || ''}
                onChange={(e) => handleChange('otherLastNames', e.target.value)}
                placeholder="Maiden name, etc."
                disabled={disabled}
              />
            </FormField>
            <FormField label="Date of Birth" required error={errors.dateOfBirth}>
              <Input
                type="date"
                value={formData.dateOfBirth || ''}
                onChange={(e) => handleChange('dateOfBirth', e.target.value)}
                disabled={disabled}
              />
            </FormField>
          </div>
        </CardContent>
      </Card>

      {/* Address */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Address</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4">
          <FormField label="Street Address" required error={errors.address?.street}>
            <Input
              value={formData.address?.street || ''}
              onChange={(e) => handleAddressChange('street', e.target.value)}
              placeholder="Street address"
              disabled={disabled}
            />
          </FormField>
          <FormField label="Apartment/Suite/Unit (if applicable)">
            <Input
              value={formData.address?.apt || ''}
              onChange={(e) => handleAddressChange('apt', e.target.value)}
              placeholder="Apt, Suite, Unit"
              disabled={disabled}
            />
          </FormField>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FormField label="City or Town" required error={errors.address?.city}>
              <Input
                value={formData.address?.city || ''}
                onChange={(e) => handleAddressChange('city', e.target.value)}
                placeholder="City"
                disabled={disabled}
              />
            </FormField>
            <FormField label="State" required error={errors.address?.state}>
              <Select
                value={formData.address?.state || ''}
                onValueChange={(value) => handleAddressChange('state', value)}
                disabled={disabled}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select state" />
                </SelectTrigger>
                <SelectContent>
                  {US_STATES.map((state) => (
                    <SelectItem key={state.code} value={state.code}>
                      {state.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="ZIP Code" required error={errors.address?.zip}>
              <Input
                value={formData.address?.zip || ''}
                onChange={(e) => handleAddressChange('zip', e.target.value)}
                placeholder="ZIP Code"
                maxLength={10}
                disabled={disabled}
              />
            </FormField>
          </div>
        </CardContent>
      </Card>

      {/* Social Security & Contact */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Identification & Contact</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              label="U.S. Social Security Number"
              required
              error={errors.ssn}
              helpText="Enter your 9-digit SSN. This is voluntary unless your employer participates in E-Verify."
            >
              <Input
                value={formData.ssn || ''}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 9);
                  const formatted = value.replace(/(\d{3})(\d{2})(\d{4})/, '$1-$2-$3');
                  handleChange('ssn', formatted);
                }}
                placeholder="XXX-XX-XXXX"
                disabled={disabled}
              />
            </FormField>
            <FormField label="Email Address" required error={errors.email}>
              <Input
                type="email"
                value={formData.email || ''}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="email@example.com"
                disabled={disabled}
              />
            </FormField>
          </div>
          <FormField label="Phone Number">
            <Input
              type="tel"
              value={formData.phone || ''}
              onChange={(e) => handleChange('phone', e.target.value)}
              placeholder="(XXX) XXX-XXXX"
              disabled={disabled}
            />
          </FormField>
        </CardContent>
      </Card>

      {/* Citizenship Status */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Citizenship/Immigration Status</CardTitle>
          <CardDescription>
            Check only one box and provide additional information if required
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <RadioGroup
            value={formData.citizenshipStatus || ''}
            onValueChange={(value) => handleChange('citizenshipStatus', value)}
            disabled={disabled}
          >
            {I9_CITIZENSHIP_STATUS.map((status) => (
              <div key={status.value} className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50">
                <RadioGroupItem value={status.value} id={status.value} />
                <Label htmlFor={status.value} className="flex-1 cursor-pointer">
                  <span className="font-medium">{status.label}</span>
                  {status.requiresAdditionalInfo && (
                    <span className="text-xs text-gray-500 ml-2">(Additional information required)</span>
                  )}
                </Label>
              </div>
            ))}
          </RadioGroup>

          {errors.citizenshipStatus && (
            <p className="text-xs text-red-500">{errors.citizenshipStatus}</p>
          )}

          {/* Additional Fields based on Status */}
          {formData.citizenshipStatus === 'lawful_permanent_resident' && (
            <div className="p-4 bg-gray-50 rounded-lg border space-y-4">
              <FormField
                label="USCIS Number or Alien Registration Number"
                required
                error={errors.uscisNumber}
              >
                <Input
                  value={formData.uscisNumber || ''}
                  onChange={(e) => handleChange('uscisNumber', e.target.value)}
                  placeholder="Enter USCIS or A-Number"
                  disabled={disabled}
                />
              </FormField>
            </div>
          )}

          {formData.citizenshipStatus === 'alien_authorized' && (
            <div className="p-4 bg-gray-50 rounded-lg border space-y-4">
              <Alert className="bg-blue-50 border-blue-200">
                <Info className="h-4 w-4 text-blue-600" />
                <AlertDescription className="text-blue-800">
                  You must provide the expiration date of your employment authorization and one of the following:
                  USCIS A-Number, Form I-94 Admission Number, or Foreign Passport Number with Country of Issuance.
                </AlertDescription>
              </Alert>

              <FormField
                label="Work Authorization Expiration Date"
                required
                error={errors.workAuthExpiration}
              >
                <Input
                  type="date"
                  value={formData.workAuthExpiration || ''}
                  onChange={(e) => handleChange('workAuthExpiration', e.target.value)}
                  disabled={disabled}
                />
              </FormField>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField label="USCIS A-Number (if applicable)">
                  <Input
                    value={formData.alienNumber || ''}
                    onChange={(e) => handleChange('alienNumber', e.target.value)}
                    placeholder="A-Number"
                    disabled={disabled}
                  />
                </FormField>
                <FormField label="Form I-94 Admission Number (if applicable)">
                  <Input
                    value={formData.i94Number || ''}
                    onChange={(e) => handleChange('i94Number', e.target.value)}
                    placeholder="I-94 Number"
                    disabled={disabled}
                  />
                </FormField>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField label="Foreign Passport Number (if applicable)">
                  <Input
                    value={formData.foreignPassportNumber || ''}
                    onChange={(e) => handleChange('foreignPassportNumber', e.target.value)}
                    placeholder="Passport Number"
                    disabled={disabled}
                  />
                </FormField>
                <FormField label="Country of Issuance (if passport provided)">
                  <Input
                    value={formData.foreignPassportCountry || ''}
                    onChange={(e) => handleChange('foreignPassportCountry', e.target.value)}
                    placeholder="Country"
                    disabled={disabled}
                  />
                </FormField>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Preparer/Translator */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Preparer and/or Translator Certification</CardTitle>
          <CardDescription>
            Complete if someone other than the employee prepared Section 1
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center space-x-3 mb-4">
            <Checkbox
              id="usedPreparer"
              checked={formData.usedPreparer || false}
              onCheckedChange={(checked) => handleChange('usedPreparer', checked)}
              disabled={disabled}
            />
            <Label htmlFor="usedPreparer" className="cursor-pointer">
              A preparer(s) and/or translator(s) assisted the employee in completing Section 1
            </Label>
          </div>

          {formData.usedPreparer && (
            <div className="p-4 bg-gray-50 rounded-lg border space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField label="Preparer's Last Name" required>
                  <Input
                    value={formData.preparerLastName || ''}
                    onChange={(e) => handleChange('preparerLastName', e.target.value)}
                    disabled={disabled}
                  />
                </FormField>
                <FormField label="Preparer's First Name" required>
                  <Input
                    value={formData.preparerFirstName || ''}
                    onChange={(e) => handleChange('preparerFirstName', e.target.value)}
                    disabled={disabled}
                  />
                </FormField>
              </div>
              <FormField label="Preparer's Address">
                <Input
                  value={formData.preparerAddress || ''}
                  onChange={(e) => handleChange('preparerAddress', e.target.value)}
                  disabled={disabled}
                />
              </FormField>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ============================================================================
// SECTION 2: EMPLOYER REVIEW AND VERIFICATION
// ============================================================================

function I9Section2({
  formData,
  onChange,
  section1Data,
  employee,
  onVerify,
  errors = {},
  disabled = false,
  isEmployee = false,
}) {
  const [documentChoice, setDocumentChoice] = useState('list_a');
  const [selectedListA, setSelectedListA] = useState(formData.listADocument?.type || '');
  const [selectedListB, setSelectedListB] = useState(formData.listBDocument?.type || '');
  const [selectedListC, setSelectedListC] = useState(formData.listCDocument?.type || '');

  // Check if documents have been uploaded (for instant display)
  const hasListAFiles = formData.listADocument?.files?.length > 0;
  const hasListBFiles = formData.listBDocument?.files?.length > 0;
  const hasListCFiles = formData.listCDocument?.files?.length > 0;

  // Auto-select based on existing data - improved to handle files without type
  useEffect(() => {
    // If List A has files or data, show List A
    if (formData.listADocument?.title || hasListAFiles || formData.listADocument?.type) {
      setDocumentChoice('list_a');
      if (formData.listADocument?.type) setSelectedListA(formData.listADocument.type);
      // If files exist but no type selected, auto-show the panel anyway
      else if (hasListAFiles && !selectedListA) {
        // Set a placeholder so the panel shows
        setSelectedListA('_has_files');
      }
    } 
    // If List B+C has files or data, show List B+C
    else if ((formData.listBDocument?.title || hasListBFiles || formData.listBDocument?.type) ||
      (formData.listCDocument?.title || hasListCFiles || formData.listCDocument?.type)) {
      setDocumentChoice('list_bc');
      if (formData.listBDocument?.type) setSelectedListB(formData.listBDocument.type);
      else if (hasListBFiles && !selectedListB) setSelectedListB('_has_files');
      if (formData.listCDocument?.type) setSelectedListC(formData.listCDocument.type);
      else if (hasListCFiles && !selectedListC) setSelectedListC('_has_files');
    }
  }, [formData, hasListAFiles, hasListBFiles, hasListCFiles, selectedListA, selectedListB, selectedListC]);

  const handleChange = (field, value) => {
    onChange({ ...formData, [field]: value });
  };

  const handleDocumentChange = (list, field, value) => {
    const docKey = `${list}Document`;
    onChange({
      ...formData,
      [docKey]: { ...formData[docKey], [field]: value },
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg">
        <Building className="h-6 w-6 text-green-600" />
        <div>
          <h3 className="font-semibold text-green-900">Section 2: Employer or Authorized Representative Review and Verification</h3>
          <p className="text-sm text-green-700">
            The employer must examine documents and complete Section 2 within 3 business days of hire.
          </p>
        </div>
      </div>

      {/* Employee Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Employee Information (from Section 1)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Name:</span>
              <p className="font-medium">{section1Data?.firstName} {section1Data?.lastName}</p>
            </div>
            <div>
              <span className="text-gray-500">Status:</span>
              <p className="font-medium">
                {I9_CITIZENSHIP_STATUS.find(s => s.value === section1Data?.citizenshipStatus)?.label || 'Not specified'}
              </p>
            </div>
            <div>
              <span className="text-gray-500">Start Date:</span>
              <p className="font-medium">{employee?.startDate || 'Not set'}</p>
            </div>
            <div>
              <span className="text-gray-500">Section 2 Due:</span>
              <p className="font-medium text-orange-600">
                {employee?.startDate ? calculateI9Section2DueDate(employee.startDate).toLocaleDateString() : 'N/A'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Uploaded Documents Summary - Show immediately if files exist */}
      {(hasListAFiles || hasListBFiles || hasListCFiles) && (
        <Card className="border-green-200 bg-green-50">
          <CardHeader>
            <CardTitle className="text-base text-green-800 flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5" />
              Employee Uploaded Documents
            </CardTitle>
            <CardDescription className="text-green-700">
              The employee has uploaded the following documents for your review. Please examine the original documents in person.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {hasListAFiles && (
              <div className="space-y-2">
                <h4 className="font-medium text-green-800 flex items-center gap-2">
                  <Badge className="bg-green-100 text-green-800">List A</Badge>
                  Identity & Work Authorization Document
                </h4>
                <div className="grid gap-2">
                  {formData.listADocument?.files?.map((file, idx) => (
                    <div key={file.id || idx} className="flex items-center gap-3 p-3 bg-white rounded-lg border">
                      {file.previewUrl ? (
                        <img src={file.previewUrl} alt={file.name} className="w-12 h-12 object-cover rounded" />
                      ) : (
                        <FileText className="h-8 w-8 text-gray-400" />
                      )}
                      <div className="flex-1">
                        <p className="font-medium text-sm">{file.name}</p>
                        <p className="text-xs text-gray-500">
                          Uploaded: {file.uploadedAt ? new Date(file.uploadedAt).toLocaleString() : 'Unknown'}
                        </p>
                      </div>
                      {file.dataUrl && (
                        <Button variant="outline" size="sm" asChild>
                          <a href={file.dataUrl} target="_blank" rel="noopener noreferrer">
                            <Eye className="h-4 w-4 mr-1" />
                            View
                          </a>
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {hasListBFiles && (
              <div className="space-y-2">
                <h4 className="font-medium text-purple-800 flex items-center gap-2">
                  <Badge className="bg-purple-100 text-purple-800">List B</Badge>
                  Identity Document
                </h4>
                <div className="grid gap-2">
                  {formData.listBDocument?.files?.map((file, idx) => (
                    <div key={file.id || idx} className="flex items-center gap-3 p-3 bg-white rounded-lg border">
                      {file.previewUrl ? (
                        <img src={file.previewUrl} alt={file.name} className="w-12 h-12 object-cover rounded" />
                      ) : (
                        <FileText className="h-8 w-8 text-gray-400" />
                      )}
                      <div className="flex-1">
                        <p className="font-medium text-sm">{file.name}</p>
                        <p className="text-xs text-gray-500">
                          Uploaded: {file.uploadedAt ? new Date(file.uploadedAt).toLocaleString() : 'Unknown'}
                        </p>
                      </div>
                      {file.dataUrl && (
                        <Button variant="outline" size="sm" asChild>
                          <a href={file.dataUrl} target="_blank" rel="noopener noreferrer">
                            <Eye className="h-4 w-4 mr-1" />
                            View
                          </a>
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {hasListCFiles && (
              <div className="space-y-2">
                <h4 className="font-medium text-blue-800 flex items-center gap-2">
                  <Badge className="bg-blue-100 text-blue-800">List C</Badge>
                  Employment Authorization Document
                </h4>
                <div className="grid gap-2">
                  {formData.listCDocument?.files?.map((file, idx) => (
                    <div key={file.id || idx} className="flex items-center gap-3 p-3 bg-white rounded-lg border">
                      {file.previewUrl ? (
                        <img src={file.previewUrl} alt={file.name} className="w-12 h-12 object-cover rounded" />
                      ) : (
                        <FileText className="h-8 w-8 text-gray-400" />
                      )}
                      <div className="flex-1">
                        <p className="font-medium text-sm">{file.name}</p>
                        <p className="text-xs text-gray-500">
                          Uploaded: {file.uploadedAt ? new Date(file.uploadedAt).toLocaleString() : 'Unknown'}
                        </p>
                      </div>
                      {file.dataUrl && (
                        <Button variant="outline" size="sm" asChild>
                          <a href={file.dataUrl} target="_blank" rel="noopener noreferrer">
                            <Eye className="h-4 w-4 mr-1" />
                            View
                          </a>
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Document Selection */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Document Verification</CardTitle>
          <CardDescription>
            The employee must present original documents. The employer must examine each document to determine if it
            reasonably appears to be genuine and relates to the person presenting it.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Selection Method */}
          <Tabs value={documentChoice} onValueChange={setDocumentChoice}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="list_a">
                One List A Document
              </TabsTrigger>
              <TabsTrigger value="list_bc">
                One List B + One List C Document
              </TabsTrigger>
            </TabsList>

            <TabsContent value="list_a" className="mt-4 space-y-4">
              <Alert className="bg-blue-50 border-blue-200">
                <Info className="h-4 w-4 text-blue-600" />
                <AlertDescription className="text-blue-800">
                  List A documents establish both identity AND employment authorization.
                </AlertDescription>
              </Alert>

              <FormField label="Select List A Document" required>
                <Select
                  value={selectedListA || ''}
                  onValueChange={setSelectedListA}
                  disabled={disabled}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select document type" />
                  </SelectTrigger>
                  <SelectContent>
                    {I9_LIST_A_DOCUMENTS.map((doc) => (
                      <SelectItem key={doc.code} value={doc.code}>
                        {doc.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>

              {selectedListA && (
                <div className="p-4 bg-gray-50 rounded-lg border space-y-4">
                  <h4 className="font-medium">Document Details</h4>
                  {/* Document Details - Only for Employer */}
                  {!isEmployee && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField label="Document Title">
                        <Input
                          value={formData.listADocument?.title || ''}
                          onChange={(e) => handleDocumentChange('listA', 'title', e.target.value)}
                          placeholder="As shown on document"
                          disabled={disabled}
                        />
                      </FormField>
                      <FormField label="Issuing Authority">
                        <Input
                          value={formData.listADocument?.issuingAuthority || ''}
                          onChange={(e) => handleDocumentChange('listA', 'issuingAuthority', e.target.value)}
                          placeholder="e.g., U.S. Department of State"
                          disabled={disabled}
                        />
                      </FormField>
                      <FormField label="Document Number">
                        <Input
                          value={formData.listADocument?.documentNumber || ''}
                          onChange={(e) => handleDocumentChange('listA', 'documentNumber', e.target.value)}
                          placeholder="Document number"
                          disabled={disabled}
                        />
                      </FormField>
                      <FormField label="Expiration Date (if any)">
                        <Input
                          type="date"
                          value={formData.listADocument?.expirationDate || ''}
                          onChange={(e) => handleDocumentChange('listA', 'expirationDate', e.target.value)}
                          disabled={disabled}
                        />
                      </FormField>
                    </div>
                  )}

                  {/* Document Upload */}
                  <DocumentUploader
                    documentType="i9_list_a"
                    documentCategory="list_a"
                    label="Upload Document Copy"
                    description="Upload a clear photo or scan of the document"
                    existingFiles={formData.listADocument?.files || []}
                    onFilesChange={(files) => handleDocumentChange('listA', 'files', files)}
                    maxFiles={2}
                    showCamera
                    disabled={disabled}
                  />
                </div>
              )}
            </TabsContent>

            <TabsContent value="list_bc" className="mt-4 space-y-6">
              <Alert className="bg-blue-50 border-blue-200">
                <Info className="h-4 w-4 text-blue-600" />
                <AlertDescription className="text-blue-800">
                  List B documents establish identity. List C documents establish employment authorization.
                  You must present one document from each list.
                </AlertDescription>
              </Alert>

              {/* List B */}
              <div className="space-y-4">
                <h4 className="font-semibold text-gray-800 flex items-center gap-2">
                  <Badge className="bg-purple-100 text-purple-800">List B</Badge>
                  Identity Document
                </h4>

                <FormField label="Select List B Document" required>
                  <Select
                    value={selectedListB || ''}
                    onValueChange={setSelectedListB}
                    disabled={disabled}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select document type" />
                    </SelectTrigger>
                    <SelectContent>
                      {I9_LIST_B_DOCUMENTS.map((doc) => (
                        <SelectItem key={doc.code} value={doc.code}>
                          {doc.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormField>

                {selectedListB && (
                  <div className="p-4 bg-purple-50 rounded-lg border border-purple-100 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {!isEmployee && (
                        <>
                          <FormField label="Document Title">
                            <Input
                              value={formData.listBDocument?.title || ''}
                              onChange={(e) => handleDocumentChange('listB', 'title', e.target.value)}
                              disabled={disabled}
                            />
                          </FormField>
                          <FormField label="Issuing Authority">
                            <Input
                              value={formData.listBDocument?.issuingAuthority || ''}
                              onChange={(e) => handleDocumentChange('listB', 'issuingAuthority', e.target.value)}
                              disabled={disabled}
                            />
                          </FormField>
                          <FormField label="Document Number">
                            <Input
                              value={formData.listBDocument?.documentNumber || ''}
                              onChange={(e) => handleDocumentChange('listB', 'documentNumber', e.target.value)}
                              disabled={disabled}
                            />
                          </FormField>
                          <FormField label="Expiration Date">
                            <Input
                              type="date"
                              value={formData.listBDocument?.expirationDate || ''}
                              onChange={(e) => handleDocumentChange('listB', 'expirationDate', e.target.value)}
                              disabled={disabled}
                            />
                          </FormField>
                        </>
                      )}
                    </div>
                    <DocumentUploader
                      documentType="i9_list_b"
                      documentCategory="list_b"
                      label="Upload List B Document"
                      existingFiles={formData.listBDocument?.files || []}
                      onFilesChange={(files) => handleDocumentChange('listB', 'files', files)}
                      maxFiles={2}
                      showCamera
                      disabled={disabled}
                    />
                  </div>
                )}
              </div>

              {/* List C */}
              <div className="space-y-4">
                <h4 className="font-semibold text-gray-800 flex items-center gap-2">
                  <Badge className="bg-orange-100 text-orange-800">List C</Badge>
                  Employment Authorization Document
                </h4>

                <FormField label="Select List C Document" required>
                  <Select
                    value={selectedListC || ''}
                    onValueChange={setSelectedListC}
                    disabled={disabled}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select document type" />
                    </SelectTrigger>
                    <SelectContent>
                      {I9_LIST_C_DOCUMENTS.map((doc) => (
                        <SelectItem key={doc.code} value={doc.code}>
                          {doc.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormField>

                {selectedListC && (
                  <div className="p-4 bg-orange-50 rounded-lg border border-orange-100 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {!isEmployee && (
                        <>
                          <FormField label="Document Title">
                            <Input
                              value={formData.listCDocument?.title || ''}
                              onChange={(e) => handleDocumentChange('listC', 'title', e.target.value)}
                              disabled={disabled}
                            />
                          </FormField>
                          <FormField label="Issuing Authority">
                            <Input
                              value={formData.listCDocument?.issuingAuthority || ''}
                              onChange={(e) => handleDocumentChange('listC', 'issuingAuthority', e.target.value)}
                              disabled={disabled}
                            />
                          </FormField>
                          <FormField label="Document Number">
                            <Input
                              value={formData.listCDocument?.documentNumber || ''}
                              onChange={(e) => handleDocumentChange('listC', 'documentNumber', e.target.value)}
                              disabled={disabled}
                            />
                          </FormField>
                          <FormField label="Expiration Date (if any)">
                            <Input
                              type="date"
                              value={formData.listCDocument?.expirationDate || ''}
                              onChange={(e) => handleDocumentChange('listC', 'expirationDate', e.target.value)}
                              disabled={disabled}
                            />
                          </FormField>
                        </>
                      )}
                    </div>
                    <DocumentUploader
                      documentType="i9_list_c"
                      documentCategory="list_c"
                      label="Upload List C Document"
                      existingFiles={formData.listCDocument?.files || []}
                      onFilesChange={(files) => handleDocumentChange('listC', 'files', files)}
                      maxFiles={2}
                      showCamera
                      disabled={disabled}
                    />
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Employer Information */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Employer Information</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField label="Employee's First Day of Employment" required error={errors.firstDayOfEmployment}>
              <Input
                type="date"
                value={formData.firstDayOfEmployment || ''}
                onChange={(e) => handleChange('firstDayOfEmployment', e.target.value)}
                disabled={disabled}
              />
            </FormField>
            <FormField label="Date of Examination (Today)" required>
              <Input
                type="date"
                value={formData.examinationDate || new Date().toISOString().split('T')[0]}
                onChange={(e) => handleChange('examinationDate', e.target.value)}
                disabled={disabled}
              />
            </FormField>
          </div>

          <FormField label="Employer's Business Name" required>
            <Input
              value={formData.employerName || ''}
              onChange={(e) => handleChange('employerName', e.target.value)}
              placeholder="Company name"
              disabled={disabled}
            />
          </FormField>

          <FormField label="Employer's Business Address" required>
            <Input
              value={formData.employerAddress || ''}
              onChange={(e) => handleChange('employerAddress', e.target.value)}
              placeholder="Street address, city, state, ZIP"
              disabled={disabled}
            />
          </FormField>
        </CardContent>
      </Card>
    </div>
  );
}

// ============================================================================
// MAIN FORM I-9 COMPONENT
// ============================================================================

export default function FormI9({
  employee,
  existingSubmission,
  onSaveSection1,
  onSubmitSection1,
  onSaveSection2,
  onVerifySection2,
  isEmployer = false, // true for admin/HR completing Section 2
  disabled = false,
  className = '',
}) {
  // Get profile data for pre-population
  const getProfileData = () => {
    const profile = employee?.profile || {};
    const address = profile.address?.mailing || {};
    return {
      firstName: profile.firstName || employee?.firstName || '',
      lastName: profile.lastName || employee?.lastName || '',
      email: employee?.email || profile.contactEmail || '',
      phone: profile.phone || '',
      dateOfBirth: profile.dateOfBirth || '',
      street: address.address1 || '',
      apt: address.address2 || '',
      city: address.city || '',
      state: address.state || '',
      zip: address.zip || '',
    };
  };

  const profileData = getProfileData();

  const [currentSection, setCurrentSection] = useState(
    existingSubmission?.section1Complete ? (isEmployer ? 2 : 1) : 1
  );
  const [section1Data, setSection1Data] = useState(existingSubmission?.section1 || {
    firstName: profileData.firstName,
    lastName: profileData.lastName,
    email: profileData.email,
    phone: profileData.phone,
    dateOfBirth: profileData.dateOfBirth,
    address: {
      street: profileData.street,
      apt: profileData.apt,
      city: profileData.city,
      state: profileData.state,
      zip: profileData.zip,
    },
  });
  const [section2Data, setSection2Data] = useState(existingSubmission?.section2 || {});
  const [section1Errors, setSection1Errors] = useState({});
  const [section2Errors, setSection2Errors] = useState({});
  const [showSignature, setShowSignature] = useState(false);
  const [signatureSection, setSignatureSection] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Calculate status
  const status = existingSubmission?.status || I9_STATUS.NOT_STARTED;
  const section1Complete = existingSubmission?.section1Complete || false;
  const section2Complete = existingSubmission?.section2Complete || false;

  // Validate Section 1
  const validateSection1 = () => {
    const errors = {};
    if (!section1Data.firstName) errors.firstName = 'First name is required';
    if (!section1Data.lastName) errors.lastName = 'Last name is required';
    if (!section1Data.dateOfBirth) errors.dateOfBirth = 'Date of birth is required';
    if (!section1Data.address?.street) errors.address = { ...errors.address, street: 'Street address is required' };
    if (!section1Data.address?.city) errors.address = { ...errors.address, city: 'City is required' };
    if (!section1Data.address?.state) errors.address = { ...errors.address, state: 'State is required' };
    if (!section1Data.address?.zip) errors.address = { ...errors.address, zip: 'ZIP code is required' };
    if (!section1Data.ssn) errors.ssn = 'Social Security Number is required';
    if (!section1Data.email) errors.email = 'Email is required';
    if (!section1Data.citizenshipStatus) errors.citizenshipStatus = 'Citizenship status is required';

    // Additional validation for work authorization
    if (section1Data.citizenshipStatus === 'lawful_permanent_resident' && !section1Data.uscisNumber) {
      errors.uscisNumber = 'USCIS Number is required for lawful permanent residents';
    }
    if (section1Data.citizenshipStatus === 'alien_authorized' && !section1Data.workAuthExpiration) {
      errors.workAuthExpiration = 'Work authorization expiration date is required';
    }

    setSection1Errors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle Section 1 Save
  const handleSaveSection1 = async () => {
    setIsSaving(true);
    try {
      await onSaveSection1?.(section1Data);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle Section 1 Submit (with signature)
  const handleSubmitSection1 = () => {
    if (!validateSection1()) return;
    setSignatureSection(1);
    setShowSignature(true);
  };

  const handleSection1Signed = async (signatureData) => {
    setIsSubmitting(true);
    try {
      await onSubmitSection1?.({
        ...section1Data,
        signature: signatureData,
        submittedAt: new Date().toISOString(),
      });
      setShowSignature(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle Section 2 Verify
  const handleVerifySection2 = () => {
    setSignatureSection(2);
    setShowSignature(true);
  };

  const handleSection2Signed = async (signatureData) => {
    setIsSubmitting(true);
    try {
      await onVerifySection2?.({
        ...section2Data,
        signature: signatureData,
        verifiedAt: new Date().toISOString(),
      });
      setShowSignature(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FileText className="h-6 w-6 text-blue-600" />
            Form I-9: Employment Eligibility Verification
          </h2>
          <p className="text-gray-600 mt-1">
            U.S. Citizenship and Immigration Services • OMB No. 1615-0047
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge
            variant="outline"
            className={
              status === I9_STATUS.COMPLETE
                ? 'bg-green-100 text-green-800 border-green-200'
                : status === I9_STATUS.PENDING_SECTION2
                  ? 'bg-yellow-100 text-yellow-800 border-yellow-200'
                  : status === I9_STATUS.SECTION1_COMPLETE
                    ? 'bg-blue-100 text-blue-800 border-blue-200'
                    : 'bg-gray-100 text-gray-800'
            }
          >
            {status === I9_STATUS.COMPLETE && <CheckCircle2 className="h-3 w-3 mr-1" />}
            {status === I9_STATUS.PENDING_SECTION2 && <Clock className="h-3 w-3 mr-1" />}
            {(status || 'not_started').replace(/_/g, ' ')}
          </Badge>
        </div>
      </div>

      {/* Progress */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-8">
              <div className={`flex items-center gap-2 ${section1Complete ? 'text-green-600' : 'text-blue-600'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${section1Complete ? 'bg-green-100' : currentSection === 1 ? 'bg-blue-100' : 'bg-gray-100'
                  }`}>
                  {section1Complete ? <Check className="h-5 w-5" /> : '1'}
                </div>
                <span className="font-medium">Section 1: Employee</span>
              </div>

              <ChevronRight className="h-5 w-5 text-gray-300" />

              <div className={`flex items-center gap-2 ${section2Complete ? 'text-green-600' : section1Complete ? 'text-blue-600' : 'text-gray-400'
                }`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${section2Complete ? 'bg-green-100' : currentSection === 2 ? 'bg-blue-100' : 'bg-gray-100'
                  }`}>
                  {section2Complete ? <Check className="h-5 w-5" /> : '2'}
                </div>
                <span className="font-medium">Section 2: Employer</span>
              </div>
            </div>
          </div>
          <Progress
            value={section2Complete ? 100 : section1Complete ? 50 : 0}
            className="h-2"
          />
        </CardContent>
      </Card>

      {/* Instructions Alert */}
      <Alert className="bg-amber-50 border-amber-200">
        <AlertTriangle className="h-4 w-4 text-amber-600" />
        <AlertTitle className="text-amber-800">Important Deadlines</AlertTitle>
        <AlertDescription className="text-amber-700">
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li><strong>Section 1:</strong> Must be completed no later than the first day of employment</li>
            <li><strong>Section 2:</strong> Must be completed within 3 business days of the first day of employment</li>
            <li>Form I-9 must be retained for 3 years after hire date or 1 year after termination, whichever is later</li>
          </ul>
        </AlertDescription>
      </Alert>

      {/* Section Tabs */}
      <Tabs value={`section${currentSection}`} onValueChange={(v) => setCurrentSection(parseInt(v.replace('section', '')))}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="section1" disabled={disabled && section1Complete}>
            <User className="h-4 w-4 mr-2" />
            Section 1: Employee
          </TabsTrigger>
          <TabsTrigger value="section2" disabled={!section1Complete}>
            <Building className="h-4 w-4 mr-2" />
            Section 2: Employer
          </TabsTrigger>
        </TabsList>

        <TabsContent value="section1" className="mt-6">
          <I9Section1
            formData={section1Data}
            onChange={setSection1Data}
            errors={section1Errors}
            disabled={section1Complete}
            employee={employee}
          />

          {!section1Complete && (
            <div className="flex justify-between mt-6">
              <Button variant="outline" onClick={handleSaveSection1} disabled={isSaving}>
                <Save className="h-4 w-4 mr-2" />
                {isSaving ? 'Saving...' : 'Save Draft'}
              </Button>
              <Button onClick={handleSubmitSection1}>
                <Send className="h-4 w-4 mr-2" />
                Sign & Submit Section 1
              </Button>
            </div>
          )}

          {section1Complete && existingSubmission?.section1?.signature && (
            <Card className="mt-6 border-green-200 bg-green-50">
              <CardHeader>
                <CardTitle className="text-green-800 flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5" />
                  Section 1 Completed
                </CardTitle>
              </CardHeader>
              <CardContent>
                <SignatureDisplay signature={existingSubmission.section1.signature} />
                <p className="text-sm text-green-700 mt-3">
                  Submitted on {new Date(existingSubmission.section1.submittedAt).toLocaleString()}
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="section2" className="mt-6">
          {!section1Complete ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Lock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900">Section 2 Locked</h3>
                <p className="text-gray-500 mt-1">
                  Section 1 must be completed by the employee before Section 2 can be accessed.
                </p>
              </CardContent>
            </Card>
          ) : (
            <>
              <I9Section2
                formData={section2Data}
                onChange={setSection2Data}
                section1Data={existingSubmission?.section1 || section1Data}
                employee={employee}
                errors={section2Errors}
                disabled={section2Complete}
                isEmployee={!isEmployer}
              />

              {!section2Complete && (
                <div className="flex justify-between mt-6">
                  {/* Button for Employees to upload documents */}
                  {!isEmployer && (
                    <Button onClick={() => onSaveSection2?.(section2Data)} disabled={isSaving}>
                      <Upload className="h-4 w-4 mr-2" />
                      {isSaving ? 'Uploading...' : 'Save Documents for HR Review'}
                    </Button>
                  )}

                  {/* Buttons for Employer */}
                  {isEmployer && (
                    <>
                      <Button variant="outline" onClick={() => onSaveSection2?.(section2Data)} disabled={isSaving}>
                        <Save className="h-4 w-4 mr-2" />
                        Save Progress
                      </Button>
                      <Button onClick={handleVerifySection2} className="bg-green-600 hover:bg-green-700">
                        <Shield className="h-4 w-4 mr-2" />
                        Verify & Complete I-9
                      </Button>
                    </>
                  )}
                </div>
              )}

              {section2Complete && existingSubmission?.section2?.signature && (
                <Card className="mt-6 border-green-200 bg-green-50">
                  <CardHeader>
                    <CardTitle className="text-green-800 flex items-center gap-2">
                      <CheckCircle2 className="h-5 w-5" />
                      Section 2 Verified
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <SignatureDisplay signature={existingSubmission.section2.signature} />
                    <p className="text-sm text-green-700 mt-3">
                      Verified on {new Date(existingSubmission.section2.verifiedAt).toLocaleString()}
                    </p>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Signature Dialog */}
      <Dialog open={showSignature} onOpenChange={setShowSignature}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <ESignature
            documentTitle={`Form I-9 - Section ${signatureSection}`}
            documentType="form_i9"
            documentId={existingSubmission?.id || 'new'}
            signerId={employee?.id}
            signerName={signatureSection === 1
              ? `${section1Data.firstName} ${section1Data.lastName}`
              : 'Authorized Representative'
            }
            signerEmail={signatureSection === 1 ? section1Data.email : employee?.employerEmail || ''}
            onSign={signatureSection === 1 ? handleSection1Signed : handleSection2Signed}
            onCancel={() => setShowSignature(false)}
            disabled={isSubmitting}
            consentText={signatureSection === 1
              ? `I attest, under penalty of perjury, that I am (check one of the following boxes):
                 
A citizen of the United States
A noncitizen national of the United States (See instructions)
A lawful permanent resident (Alien Registration Number/USCIS Number):
An alien authorized to work until (expiration date, if applicable):

I attest, under penalty of perjury, that the information I have provided is true and correct. I am aware that providing false or fraudulent documentation or making false statements may subject me to criminal penalties under 18 U.S.C. § 1546 and/or civil penalties under 8 U.S.C. § 1324c, including fines and imprisonment.`
              : `I attest, under penalty of perjury, that (1) I have examined the document(s) presented by the above-named employee, (2) the above-listed document(s) appear to be genuine and to relate to the employee named, and (3) to the best of my knowledge the employee is authorized to work in the United States.`
            }
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================================
// I-9 STATUS CARD (For Dashboard Display)
// ============================================================================

export function I9StatusCard({ submission, employee, onView, onComplete }) {
  const status = submission?.status || I9_STATUS.NOT_STARTED;
  const section2DueDate = employee?.startDate
    ? calculateI9Section2DueDate(employee.startDate)
    : null;
  const isOverdue = section2DueDate && new Date() > section2DueDate && status !== I9_STATUS.COMPLETE;

  return (
    <Card className={`transition-all hover:shadow-md ${isOverdue ? 'border-red-200 bg-red-50' : ''}`}>
      <CardContent className="pt-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${status === I9_STATUS.COMPLETE
              ? 'bg-green-100'
              : isOverdue
                ? 'bg-red-100'
                : 'bg-blue-100'
              }`}>
              <FileText className={`h-5 w-5 ${status === I9_STATUS.COMPLETE
                ? 'text-green-600'
                : isOverdue
                  ? 'text-red-600'
                  : 'text-blue-600'
                }`} />
            </div>
            <div>
              <h4 className="font-medium text-gray-900">
                {employee?.profile?.firstName || employee?.firstName} {employee?.profile?.lastName || employee?.lastName}
              </h4>
              <p className="text-sm text-gray-500">
                Start Date: {employee?.profile?.hireDate || employee?.startDate ? new Date(employee?.profile?.hireDate || employee?.startDate).toLocaleDateString() : 'Not set'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Badge
              variant="outline"
              className={
                status === I9_STATUS.COMPLETE
                  ? 'bg-green-100 text-green-800 border-green-200'
                  : isOverdue
                    ? 'bg-red-100 text-red-800 border-red-200'
                    : status === I9_STATUS.PENDING_SECTION2
                      ? 'bg-yellow-100 text-yellow-800 border-yellow-200'
                      : 'bg-gray-100'
              }
            >
              {isOverdue && <AlertTriangle className="h-3 w-3 mr-1" />}
              {(status || 'not_started').replace(/_/g, ' ')}
            </Badge>

            <div className="flex gap-1">
              <Button variant="ghost" size="sm" onClick={() => onView?.(submission)}>
                <Eye className="h-4 w-4" />
              </Button>
              {status !== I9_STATUS.COMPLETE && (
                <Button variant="ghost" size="sm" onClick={() => onComplete?.(submission)}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </div>

        {isOverdue && (
          <Alert className="mt-3 bg-red-100 border-red-200">
            <AlertTriangle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-800">
              Section 2 was due by {section2DueDate.toLocaleDateString()}. Please complete immediately.
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}
